<!-- Timeline component (Job)                                        -->

<!-- Job statuses are displayed in the order provided by backend.    -->
<!-- Clickable:                                                      -->
<!--     - arrow down to activate status (current time)              -->
<!--     - full dot to edit the date of the status with dialog       -->

<!-- PROPS: 'expectedCompletion, date: "..."                         -->
<!-- PROPS: 'timeline, [{ date: "...", status: { name: "..." } }]'   -->
<!-- PROPS: 'statuses, [{ name: "..." }]'                            -->
<!-- EMITS: 'edit, [Cost]' when the user saves the changes           -->
<!-- EMITS: 'editComp, Date() (iso)' on expectedCompletion update    -->

<!-- eslint-disable vue/max-attributes-per-line -->
<template>
  <!-- Display a light border -->
  <div id="main">
    <!-- Display a light dividing border -->
    <!-- Red background if expected completion date is set to past date -->
    <div :class="['completion-box', { overdue: isOverdue }]">
      <v-layout align-center justify-space-between>
        <!-- Title of first section -->
        <div>Expected completion</div>
        <!-- Edit and reset buttons -->
        <div>
          <v-btn class="ma-0" icon small @click="setExpectedCompletion">
            <v-icon small>edit</v-icon>
          </v-btn>
          <v-btn class="ma-0" icon small @click="resetExpectedCompletion">
            <v-icon small>remove</v-icon>
          </v-btn>
        </div>
      </v-layout>
      <!-- Subtitle: `date` (from now + tooltip) / "Not set" -->
      <v-layout justify-center>
        <div
          v-if="expectedCompletion"
          :class="[{'green--text': !isOverdue}, 'text--lighten-3']">
          <v-tooltip transition="scale-transition" left>
            <span slot="activator">{{ expectedCompletion | formatFn }}</span>
            <span>{{ expectedCompletion | formatLLL }}</span>
          </v-tooltip>
        </div>
        <div v-else class="orange--text text--lighten-3">Not set</div>
      </v-layout>
    </div>
    <!-- Main section -->
    <v-layout class="pa-4">
      <v-flex offset-xs4 xs8>
        <!-- Part I: display the events (statuses with a date) -->
        <template v-for="stEvt in timeline">
          <v-layout :key="stEvt.name">
            <div class="parent">
              <v-tooltip transition="scale-transition" left>
                <span
                  slot="activator"
                  class="dot full clickable"
                  @click="change(stEvt)"/>
                <span>{{ stEvt.date | formatLLL }}</span>
              </v-tooltip>
              <span class="ml-3">{{ stEvt.date | formatFn }}</span>
              <div class="ml-3 green--text text--lighten-3">
                {{ stEvt.status.name }}
              </div>
            </div>
          </v-layout>
        </template>
        <!-- Part II: display the other statuses (to be given a date) -->
        <template v-for="(st, index) in remainingStatuses">
          <v-layout :key="st.name">
            <div class="parent dashed">
              <v-tooltip transition="scale-transition" left>
                <!-- Arrow next to first remaining status -->
                <span
                  v-if="index === 0"
                  slot="activator"
                  class="arrow clickable"
                  @click="activate(st.id)"/>
                <span>Activate status</span>
              </v-tooltip>
              <span class="dot outlined"/>
              <span class="ml-3">N/A</span>
              <div class="ml-3 orange--text text--lighten-3">{{ st.name }}</div>
            </div>
          </v-layout>
        </template>
      </v-flex>
    </v-layout>
    <!-- DATE PICKER DIALOG -->
    <v-dialog v-model="dialog" max-width="50rem">
      <v-card>
        <v-card-text>
          <v-container>
            <v-layout>
              <v-card flat>
                <v-card-text>
                  <v-date-picker
                    ref="picker"
                    v-model="editedDate.date"
                    full-width
                    landscape
                    class="mb-1"/>
                  <v-time-picker
                    ref="picker"
                    v-model="editedDate.time"
                    full-width
                    landscape/>
                </v-card-text>
              </v-card>
            </v-layout>
          </v-container>
        </v-card-text>
        <v-card-actions>
          <v-spacer/>
          <v-btn flat @click="dialog = false">Close</v-btn>
          <v-btn flat @click="save">Save</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import moment from "moment";

export default {
  name: "timeline",
  props: {
    expectedCompletion: {
      type: String,
      default: null
    },
    statuses: {
      type: Array,
      default: () => []
    },
    timeline: {
      type: Array,
      default: () => []
    }
  },
  data () {
    return {
      dialog: false,
      editedStId: "",
      editedDate: {}
    };
  },
  computed: {
    isOverdue () {
      // Job not completed
      return this.timeline.length < this.statuses.length &&
      // Job's expected completion date over
        this.expectedCompletion < new Date().toISOString();
    },
    remainingStatuses () {
      // Filter statuses: keep statuses not in timeline
      return this.statuses.filter((st) => this.timeline.
        map((stEvt) => stEvt.status.id).
        indexOf(st.id) === -1
      );
    }
  },
  methods: {
    change (stEvt) {
      // A date has been set for a status. Change this date: open dialog.
      this.editedDate = this.isoToObject(stEvt.date);
      this.editedStId = stEvt.status.id;
      this.dialog = true;
    },
    setExpectedCompletion () {
      this.editedDate = this.isoToObject(this.expectedCompletion);
      this.editedStId = -1;
      this.dialog = true;
    },
    /*
     * 2 functions to ISO <=> { date: "YYYY-MM-DD", time: "HH:mm" }
     * ISO is used on bakcend
     * { date: "YYYY-MM-DD", time: "HH:mm" } is used by Vuetify pickers
     */
    isoToObject (iso) {
      // Function 1
      if (!iso) {
        return {
          time: "10:00",
          date: ""
        };
      }
      return {
        time: moment(iso).format("HH:mm"),
        date: moment(iso).format("YYYY-MM-DD")
      };
    },
    objectToIso (o) {
      // Function 2
      if (!o.date) {
        return null;
      }
      return moment().
        year(parseInt(o.date.substring(0, 4), 10)).
        month(parseInt(o.date.substring(5, 7), 10) - 1).
        date(parseInt(o.date.substring(8, 10), 10)).
        hours(parseInt(o.time.substring(0, 2), 10)).
        minutes(parseInt(o.time.substring(3, 5), 10)).
        toISOString();
    },
    resetExpectedCompletion () {
      this.$emit("editExpCompDate", null);
    },
    save () {
      // When the SAVE button of the edition dialog is clicked
      if (this.editedStId === -1) {
        this.$emit("editExpCompDate", this.objectToIso(this.editedDate));
        this.dialog = false;
      } else {
        this.$emit(
          "updateStatusEvent",
          this.editedStId,
          this.objectToIso(this.editedDate)
        );
        this.dialog = false;
      }
    },
    activate (id) {
      // When the arrow next to an inactive status is clicked
      this.$emit("activateStatus", id, moment().toISOString());
    }
  }
};
</script>

<style scoped>
.completion-box { border-bottom: 1px solid #494949; padding: 0.5rem 1rem; }
.completion-box.overdue { background-color: red; }
#main { border: 1px solid #494949; }
.parent {
  position: relative;
  border-left: 2px solid;
  padding-bottom: 0.6rem;
}
.parent.dashed { border-left-style: dashed; }
.dot {
  height: 10px;
  width: 10px;
  border-radius: 50%;
  position: absolute;
  display: inline-block;
}
.dot.full { background-color: white; top: 4px; left: -6px; }
.dot.outlined {
  border: 2px white solid;
  background-color: #303030;
  top: 5px;
  left: -6px;
}
.arrow {
  color: transparent;
  border: solid white;
  border-width: 0 2px 2px 0;
  display: inline-block;
  height: 10px;
  width: 10px;
  position: absolute;
  top: 0px;
  left: -33px;
  display: inline-block;
  transform: rotate(45deg);
}
.v-card.v-picker { box-shadow: none; }
</style>



// WEBPACK FOOTER //
// src/components/Job/Timeline.vue